import React, { useEffect, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCServicesApi, RCCTooltip, arrayMove } from "react-common-components";
import { useForm } from "react-hook-form";
import Accordion from "src/components/Accordion";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import FiltersContainer from "src/components/FiltersContainer";
import Loader from "src/components/Loader";
import LovSectors from "src/components/Lov/Sectors";
import LovUsers from "src/components/Lov/Users";
import Select from "src/components/Select";
import { useTranslation } from "src/context/Translations/context";
import { ActionGroupers, ActionSituation, createIGroupersActions, IGroupersActions } from "src/pages/Actions/Panel/interface";
import { standardErrorMessages } from "src/utils/errors";
import SortableContainer from "src/components/Sortable/Container";
import SortableItem from "src/components/Sortable/Item";
import formatDate from "src/utils/formatDate";
import CopyIniciativesModal from "src/components/ModalIniciatives/Iniciatives/copyIniciative";
import extractSingleValueFromArrayOfObject from "src/utils/extractSingleValueFromArrayOfObject";
import { getLocalStorage, setLocalStorage } from "src/utils/localStorage";
import { useAuth } from "src/context/Auth/context";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { restrictToVerticalAxis } from "src/components/Sortable/Container/modifiers";
import { IFiltersIniciativesPanel, IIniciatives, IIntba001 } from "./interface";
import { ContainerInput } from "./styles";
import { IRefIniciativeCopyModals } from "../interface";
import GroupersOfIniciatives from "./groupersOfIniciative";
import ModalIniciativeReleaseActions, { IIniciativeReleaseActionsRef } from "./modalReleaseActions";

const Iniciatives: React.FC<IIniciatives>= ({ 
	idKr,
	idObjective,
	permissions,
	subtitle
}) => {
	const { translate } = useTranslation(); 
	const { addToast } = RCCHookToast(); 
	const { user } = useAuth();
	const { administrator, superUser } = useAuth();

	const modalCopyIniciativeRef = useRef<IRefIniciativeCopyModals>(null);
	const modalReleaseActionsRef = useRef<IIniciativeReleaseActionsRef>(null);
	const IniciativeInputDivRef = useRef<HTMLDivElement>(null);

	const [iniciatives, setIniciatives] = useState<IGroupersActions[]>([]);
	const [sortableIniciatives, setSortableIniciatives] = useState<{
		id: string
	}[]>([]);

	const [loading, setLoading] = useState(false);
	const [isAdd, setIsAdd] = useState(false);
	const [errorLoading, setErrorLoading] = useState(false);

	const [actionsInCard, setActionsInCard] = useState(getLocalStorage(`IniciativeScreenLayoutList-${user.efa005Id}`) !== "list");
	
	const { watch, register, setValue } = useForm();

	/** FILTERS */
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<IFiltersIniciativesPanel>({ 
		f: "", 
		responsible: "",
		authority: "",
		consulted: "",
		informed: "",
		sector: "",
		situation: "",
		startDate: null,
		endDate: null,
		tags: ""
	});
	const { 
		handleSubmit : handleSubmitFilters, 
		register : registerFilters, 
		control: controlFilters, 
		reset: resetFilters, 
		setValue:setValueFilters
	} = useForm();

	useEffect(() => {getIniciatives();}, [filters]);
	useEffect(() => {
		setSortableIniciatives(iniciatives.map((iniciative) => ({
			id: iniciative.id.toString()
		})));
	}, [iniciatives]);

	const submitFilters = (data: any) => {
		const dataForm = data;
		if(data.situation)
			dataForm.situation = extractSingleValueFromArrayOfObject("value", data.situation)?.toString();
		
		if(typeof data.authority === "object")
			dataForm.authority = data.authority.value;
		if(typeof data.consulted === "object")
			dataForm.consulted = data.consulted.value;
		if(typeof data.informed === "object")
			dataForm.informed = data.informed.value;
		if(typeof data.responsible === "object")
			dataForm.responsible = data.responsible.value;
		if(typeof data.sector === "object")
			dataForm.sector = data.sector.value;

		setFilters(dataForm);
	};

	async function getIniciatives(){
		setLoading(true);
		setErrorLoading(false);

		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "Iniciative/List",
				urlParameters: {
					group: ActionGroupers.INICIATIVE,
					idKr: idKr || "",
					idObjective: idObjective || "",
					origin: "",
					delayed: "",
					f: filters.f || "",
					responsible: filters.responsible || "",
					authority: filters.authority || "",
					consulted: filters.consulted || "",
					informed: filters.informed || "",
					sector: filters.sector || "",
					situation: filters.situation || "",
					startDate: filters.startDate ? formatDate(filters.startDate) : "",
					endDate: filters.endDate ? formatDate(filters.endDate) : "",
					tags: ""
				}
			});

			const iniciative = data as IGroupersActions[];
			setIniciatives(iniciative);
			setLoading(false);

		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar as iniciativas"), addToast);
			setLoading(false);
			setErrorLoading(true);
		}
	}

	const handleDrag = (activeId: string, overId: string) => {
		const activeIndex = iniciatives.findIndex((iniciative) => String(iniciative.id) === activeId);
		const overIndex = iniciatives.findIndex((iniciative) => String(iniciative.id) === overId);
		
		const oldIniciatives = iniciatives;
		let updateIniciatives = iniciatives;
		updateIniciatives = arrayMove(updateIniciatives, 
			activeIndex, 
			overIndex);

		
		updateOrderIniciatives(updateIniciatives, oldIniciatives);
	};

	const addIniciative = () => {
		setIsAdd(true);
	};

	const cancelIniciative = () => {
		setIsAdd(false);
		setValue("titleAddIniciative", "");	
	};

	async function insertIniciative(){
		try{
			const title = watch("titleAddIniciative");
			if(!title || title === ""){
				addToast({
					title: translate("Erro"),
					description: translate("É necessário inserir um titulo"),
					color: "yellow"
				});
				return;
			}

			const {data} = await RCCServicesApi.postRequest({
				method: "Iniciative/Insert",
				urlParameters: {
					title,
					idKr: idKr || "",
					idObjective: idObjective || ""
				}
			});
			const intba001 = data as IIntba001;

			const iniciative = createIGroupersActions({
				id: intba001.ina001Id, 
				title: intba001.ina001Titulo
			} as IGroupersActions);

			setIsAdd(false);
			setIniciatives([...iniciatives, {
				...iniciative,
				title
			}]);
			setValue("titleAddIniciative", "");

			addToast({
				color: "green",
				description: translate("Iniciativa Inserida com sucesso"),
				title: translate("Sucesso")
			});

		}catch(err){
			standardErrorMessages(err, translate("Erro ao adicionar iniciativa"), addToast);
		}
	}

	async function updateOrderIniciatives(updateIniciatives: IGroupersActions[], oldIniciatives: IGroupersActions[]){
		try{
			// foi colocado "setIniciatives" antes da "request" porque demora pra atulizar no backend, dai a transição dos cards fica visualmente ruim
			// caso der erro da um rollback no catch
			setIniciatives(updateIniciatives);
		
			await RCCServicesApi.putRequest({
				method: "Iniciative/UpdateOrder",
				body: {
					iniciatives: updateIniciatives.map((inic: IGroupersActions, index) => ({
						ina001Id: inic.id,
						ina001Ordem: index+1
					}))
				}
			});			

			addToast({
				color: "green",
				description: translate("Iniciativas ordenadas com sucesso"),
				title: translate("Sucesso")
			});

		}catch(err){
			// rollback
			setIniciatives(oldIniciatives);
			standardErrorMessages(err, translate("Erro ao ordenar iniciativas"), addToast);
		}
	}

	const addAccordionIniciative = () => (
		<RCCGrid
			container
			justifyContent="space-between"
			styleInline="margin-bottom:20px"
			
		>
			<RCCGrid
				container
				item
				xs={10}
			>
				<ContainerInput 
					ref={IniciativeInputDivRef}
				>
					<RCCInput
						name="titleAddIniciative" 
						register={register}
						label={translate("Nome da Iniciativa")}
						defaultValue=""
						onClick={(e) => {e.stopPropagation();}}
						maxLength={200}
					/>
				</ContainerInput>
				
			</RCCGrid>
			<RCCGrid
				container
				item
				justifyContent="flex-end"
				alignItems="center"
				xs={2}
			>
				<RCCTooltip
					text={translate("Adicionar")}
					color="green"
				>
					<RCCButton
						icon={{name: "check", color: "#FFF", size: 10}}
						color="green"
						size="xs"
						onClick={(e) => {e.stopPropagation(); insertIniciative();}}
					/>
				</RCCTooltip>
				<RCCTooltip
					text={translate("Cancelar")}
					color="red"
				>
					<RCCButton
						icon={{name: "close", color: "#FFF", size: 10}}
						color="red"
						size="xs"
						onClick={(e) => {e.stopPropagation(); cancelIniciative();}}
					/>
				</RCCTooltip>
			</RCCGrid>
		</RCCGrid>
	);

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				{/* Header -> Results + Buttons */}
				<RCCGrid
					container
					alignContent="flex-end"
					justifyContent="flex-end"
					styleInline="margin-bottom: 5px;"
				>

				

					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						{(permissions >= 2 || administrator || superUser) && (
							<>
								<RCCTooltip
									text={translate("Nova Iniciativa")}
									color="purple"
									position="bottom"
								>
									<RCCButton
										icon={{name: "plus", color: "#FFF", size: 18}}
										color="purple"
										layout="isRounded"
										onClick={(e) => {e.stopPropagation(); addIniciative();}}
										size="md"
									/>
								</RCCTooltip>
								<RCCTooltip
									text={translate("Copiar Iniciativas")}
									color="grey"
									position="bottom"
								>
									<RCCButton
										icon={{name: "copy", color: "#FFF", size: 18}}
										color="grey"
										layout="isRounded"
										onClick={(e) => {e.stopPropagation(); modalCopyIniciativeRef.current?.openModalCopyIniciative({idKr, idObjective});}}
										size="md"
									/>
								</RCCTooltip>
							</>
						)}											
					</CheckPermission>

					<RCCTooltip
						text={actionsInCard ? translate("Alterar para lista") : translate("Alterar para cards")}
						color="grey"
						position="bottom"
					>
						<RCCButton
							icon={{
								name: actionsInCard ? "list" : "table", 
								color: "#FFF", 
								size: 16
							}}
							color="grey"
							layout="isRounded"
							size="md"
							onClick={() => {
								if(actionsInCard){
									setActionsInCard(false);
									setLocalStorage(`IniciativeScreenLayoutList-${user.efa005Id}`, "list");
									return;
								}
								setLocalStorage(`IniciativeScreenLayoutList-${user.efa005Id}`, "card");
								setActionsInCard(true);
							}}
						/>
					</RCCTooltip>
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters ||
							filters.authority ||
							filters.consulted || 
							filters.endDate || 
							filters.f || 
							filters.informed ||
							filters.responsible || 
							filters.sector || 
							filters.situation || 
							filters.startDate
						 	? "blue" : "grey"}
						position="bottom"
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters ||
								filters.authority ||
								filters.consulted || 
								filters.endDate || 
								filters.f || 
								filters.informed ||
								filters.responsible || 
								filters.sector || 
								filters.situation || 
								filters.startDate
							 	? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</RCCGrid>

				{/* Filters */}
				<FiltersContainer
					showFilters={showFilters}
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters({ 
						f: "", 
						responsible: { value: "", label: ""},
						authority: { value: "", label: ""},
						consulted: { value: "", label: ""},
						informed: { value: "", label: ""},
						sector: { value: "", label: ""},
						situation: [{
							label: translate("Concluída"),
							value: ActionSituation.FINISHED
						},{
							label: translate("Em andamento"),
							value: ActionSituation.IN_PROGRESS
						},{
							label: translate("Não Iniciada"),
							value: ActionSituation.NOT_STARTED
						}],
						startDate: null,
						endDate: null
					})}
				>
					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<DateTimePickerRange
								control={controlFilters}
								label={translate("Período")}
								labelSeparator={translate("até")}
								nameInit = "startDate"
								nameEnd = "endDate"
								defaultValueEnd={filters.endDate}
								defaultValueInit={filters.startDate}
								type="datePicker"
								setValue={setValueFilters}
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovUsers 
								control={controlFilters}
								name="authority"
								label={translate("Autoridade")}
								defaultValue={filters.authority}
								isClearable
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovUsers 
								control={controlFilters}
								name="responsible"
								label={translate("Responsável")}
								defaultValue={filters.responsible}
								isClearable
							/>
						</RCCGrid>
					</RCCGrid>

					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovUsers 
								control={controlFilters}
								name="consulted"
								label={translate("Consultado")}
								defaultValue={filters.consulted}
								isClearable
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovUsers 
								control={controlFilters}
								name="informed"
								label={translate("Informado")}
								defaultValue={filters.informed}
								isClearable
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovSectors 
								control={controlFilters}
								name="sector"
								label={translate("Setor")}
								defaultValue={filters.sector}
								isClearable
							/>
						</RCCGrid>
					</RCCGrid>

					<RCCGrid
						container
						item
						spacing={1}
						style={{marginBottom: "1.5rem"}}
					>
					
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<Select 
								control={controlFilters}
								name="situation"
								label={translate("Situação")}
								defaultValue={filters.situation || [{
									label: translate("Concluída"),
									value: ActionSituation.FINISHED
								},{
									label: translate("Em andamento"),
									value: ActionSituation.IN_PROGRESS
								},{
									label: translate("Não Iniciada"),
									value: ActionSituation.NOT_STARTED
								}]}
								options={[{
									label: translate("Cancelada"),
									value: ActionSituation.CANCELED
								},{
									label: translate("Em Rascunho"),
									value: ActionSituation.DRAFT
								},{
									label: translate("Concluída"),
									value: ActionSituation.FINISHED
								},{
									label: translate("Em andamento"),
									value: ActionSituation.IN_PROGRESS
								},{
									label: translate("Não Iniciada"),
									value: ActionSituation.NOT_STARTED
								}]}
								isMulti
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<RCCInput
								name="f" 
								label={translate("Pesquisar")}
								register={registerFilters}
								defaultValue={filters.f}
							/>
						</RCCGrid>
					</RCCGrid>            
				</FiltersContainer>
            
				{
					isAdd && 
                	<Accordion 
                		showExpandIcon={false}
                		header={
                			<>
                				{addAccordionIniciative()}
                			</>
                		}               		
                	>
                	</Accordion>
				}
				{
					loading ? 
						(<Loader />) : 
						(<>
							{
								errorLoading ? 
									(
										<RCCAlert 
											title={translate("Atenção")}
											description={translate("Erro ao buscar as iniciativas")}
										/>
									) : (
										<>
											{
												iniciatives && iniciatives.length === 0 
													? (<RCCAlert 
														title={translate("Atenção")}
														description={translate("Nenhuma Iniciativa encontrada")}
													/>)
													: 
													(<>
														<SortableContainer
															items={sortableIniciatives || []}
															onDragEnd={handleDrag}
															modifiers={[restrictToVerticalAxis]}
														>
															{
																iniciatives && iniciatives.map((iniciative: IGroupersActions) => (
																	<SortableItem id={String(iniciative.id)} key={iniciative.id}>
																		<GroupersOfIniciatives 
																			actionsInCard={actionsInCard}
																			filters={filters}
																			permissions={permissions}
																			grouper={iniciative}
																			onCallbackAdd={getIniciatives}
																			onCallbackDelete={(id: number) => setIniciatives((prevState) => prevState.filter((ini) => ini.id !== id))}
																			onUpdateTitle={(id: number, title: string) => {
																				setIniciatives((prevState) => {
																					const index = prevState.findIndex((ini) => ini.id === id);
																					if(index === -1)
																						return prevState;
																					const updateState = prevState;
																					updateState[index].title = title;
																					return updateState;
																				});
																			}}
																			openModalReleaseActions={modalReleaseActionsRef.current?.openModalReleaseActions}
																		/>
																	</SortableItem>
			
																))
															}
														</SortableContainer>
													</>) 
											}
										</>
									)
							}
						</>)   
				}
			</RCCGrid>

			<CopyIniciativesModal subtitle={subtitle} ref={modalCopyIniciativeRef} onCallbackCopyIniciative={() => getIniciatives()}/>

			<ModalIniciativeReleaseActions ref={modalReleaseActionsRef} />
		</>
	);
};

export default Iniciatives;
